/* ============================================================
   แถบเมนูด้านบน — ใช้แทน Sidebar เดิม

   ยังไม่ได้เลือกกลุ่มงาน : แสดงรายชื่อกลุ่มงานทั้งหมด
   เลือกกลุ่มงานแล้ว      : แสดงเมนูย่อยของกลุ่มนั้นแทน
                            (มีปุ่มซ้ายสุดสำหรับย้อนกลับไปเลือกกลุ่ม)
============================================================ */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Flex } from "antd";
import logo from "../../assets/images/logo_ak_mark.png";
import HeaderUser from "./HeaderUser";
import {
  AppstoreOutlined,
  ClockCircleOutlined,
  LeftOutlined,
  RightOutlined,
  LockOutlined,
  PushpinFilled,
} from "@ant-design/icons";

import { usePermission } from "../../store/context/permission-context";
import { Authenticate } from "../../service/Authenticate.service";
import { getRecent, pushRecent } from "../../utils/recentRecord";
import { getPinned } from "../../utils/pinnedMenu";
import nav from "../../nav";
import { buildGroups, findGroupByPath } from "../../utils/navGroups";

export default function ModuleBar() {
  const { pathname, state } = useLocation();
  const { canView } = usePermission();

  const groups = useMemo(() => buildGroups(canView), [canView]);

  /* จำหน้าที่เข้าล่าสุดไว้ใน cookie เพื่อทำทางลัดในหน้าเมนูหลัก */
  useEffect(() => {
    const matched = nav
      .filter((n) => n.to && pathname.startsWith(n.to))
      .sort((a, b) => b.to.length - a.to.length)[0];
    if (matched) {
      pushRecent(Authenticate().getUserId(), {
        title: matched.title,
        to: matched.to,
      });
    }
  }, [pathname]);

  /* เข้ามาจากทางลัด "เข้าใช้ล่าสุด" — แถบบนแสดงกลุ่มเมนูล่าสุดทั้งหมด */
  const fromRecent = !!state?.fromRecent;
  const recentItems = fromRecent
    ? getRecent(Authenticate().getUserId()).filter((r) =>
        groups.some((g) => g.items.some((i) => i.to === r.to))
      )
    : [];

  /* เข้ามาจากเมนูปักหมุด — แถบบนแสดงเมนูปักหมุดทั้งหมด */
  const fromPinned = !!state?.fromPinned;
  const pinnedItems = fromPinned
    ? getPinned(Authenticate().getUserId()).filter((p) =>
        groups.some((g) => g.items.some((i) => i.to === p.to && i.allowed))
      )
    : [];

  const activeByPath = findGroupByPath(groups, pathname);
  const keyFromUrl =
    pathname.startsWith("/modules/") ? pathname.split("/")[2] : null;
  const group =
    groups.find((g) => g.key === keyFromUrl) || activeByPath || null;

  const chip = (active, extra) => ({
    cursor: "pointer",
    userSelect: "none",
    height: 34,
    padding: "0 16px",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: 0.2,
    whiteSpace: "nowrap",
    color: active ? "#fff" : "#5a3d22",
    background: active
      ? "linear-gradient(135deg, #c08a35 0%, #8a5a1c 100%)"
      : "#faf6ef",
    border: `1px solid ${active ? "transparent" : "#eadfcd"}`,
    boxShadow: active ? "0 4px 10px rgba(47,95,179,.28)" : "none",
    transition: "all .18s ease",
    display: "inline-flex",
    alignItems: "center",
    textDecoration: "none",
    flex: "0 0 auto",
    ...extra,
  });

  /* แถบเมนูตรงกลาง — ถ้าเมนูยาวเกินจอ ให้เลื่อนซ้าย/ขวาแทนการตัดบรรทัด */
  const MenuStrip = ({ children }) => {
    const ref = useRef(null);
    const [over, setOver] = useState({ left: false, right: false });

    const update = useCallback(() => {
      const el = ref.current;
      if (!el) return;
      setOver({
        left: el.scrollLeft > 4,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
      });
    }, []);

    useEffect(() => {
      update();
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }, [update]);

    const scrollBy = (dir) => {
      const el = ref.current;
      if (el) el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.7), behavior: "smooth" });
    };

    const arrow = {
      flex: "0 0 auto",
      width: 28,
      height: 30,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 8,
      border: "1px solid #eadfcd",
      background: "#fff",
      color: "#5a3d22",
      cursor: "pointer",
    };

    return (
      <div style={{ flex: "1 1 auto", minWidth: 0, display: "flex", alignItems: "center", gap: 6 }}>
        {over.left ? (
          <span style={arrow} onClick={() => scrollBy(-1)}>
            <LeftOutlined />
          </span>
        ) : null}

        <div
          ref={ref}
          onScroll={update}
          className="module-strip"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            overflowX: "auto",
            flex: "1 1 auto",
            minWidth: 0,
            scrollbarWidth: "none",
          }}
        >
          {children}
        </div>

        {over.right ? (
          <span style={arrow} onClick={() => scrollBy(1)}>
            <RightOutlined />
          </span>
        ) : null}
      </div>
    );
  };

  /* โลโก้บริษัทมุมซ้ายบน กดกลับหน้าเมนูหลัก */
  const Brand = () => (
    <Link
      to="/modules"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        marginRight: 2,
        textDecoration: "none",
        whiteSpace: "nowrap",
        flex: "0 0 auto",
      }}
    >
      <img src={logo} alt="AK-HOME" style={{ height: 34, width: "auto", borderRadius: 6 }} />
      <span
        style={{ fontSize: 15, fontWeight: 800, color: "#8a5a1c", letterSpacing: 0.2 }}
      >
        AK-HOME
      </span>
    </Link>
  );

  /* ชิปเมนูที่ไม่มีสิทธิ์ — สีเทา กดไม่ได้ */
  const deniedChip = {
    ...chip(false),
    cursor: "not-allowed",
    color: "#9aa5b4",
    background: "#f2f4f7",
    borderColor: "#e3e7ee",
  };

  const bar = {
    minHeight: 60,
    padding: "10px 20px",
    background: "#fff",
    borderBottom: "1px solid #eee5d6",
    boxShadow: "0 2px 10px rgba(15,23,42,.05)",
    position: "sticky",
    top: 0,
    zIndex: 20,
  };

  /* เส้นคั่นบาง ๆ ระหว่างโลโก้กับชุดเมนู */
  const divider = (
    <span
      style={{
        width: 1,
        height: 26,
        background: "#f1e9dc",
        margin: "0 4px",
        flex: "0 0 auto",
      }}
    />
  );

  /* มาจากเมนูปักหมุด — แสดงเมนูปักหมุดทั้งหมดบนแถบ */
  if (fromPinned && pinnedItems.length > 0) {
    return (
      <Flex align="center" gap={10} wrap={false} style={bar}>
        <Brand />
        {divider}
        <MenuStrip>
          <Link
            to="/modules"
            style={chip(false, { background: "#fff7e6", borderColor: "#ffe0a3", color: "#b3720a" })}
          >
            <PushpinFilled style={{ marginRight: 6 }} />
            เมนูปักหมุด
          </Link>
          {pinnedItems.map((p) => (
            <Link
              key={p.to}
              to={p.to}
              state={{ fromPinned: true }}
              style={chip(pathname.startsWith(p.to))}
            >
              {p.title}
            </Link>
          ))}
        </MenuStrip>
        <span style={{ flex: "0 0 auto" }}>
          <HeaderUser />
        </span>
      </Flex>
    );
  }

  /* มาจากทางลัด — แสดงเมนูที่เข้าใช้ล่าสุดทั้งหมดบนแถบ */
  if (fromRecent && recentItems.length > 0) {
    return (
      <Flex align="center" gap={10} wrap={false} style={bar}>
        <Brand />
        {divider}
        <MenuStrip>
        <Link
          to="/modules"
          style={chip(false, { background: "#f5f7fa", borderColor: "#e2e8f0" })}
        >
          <ClockCircleOutlined style={{ marginRight: 6 }} />
          เข้าใช้ล่าสุด
        </Link>
        {recentItems.map((r) => (
          <Link
            key={r.to}
            to={r.to}
            state={{ fromRecent: true }}
            style={chip(pathname.startsWith(r.to))}
          >
            {r.title}
          </Link>
        ))}
        </MenuStrip>
        <span style={{ flex: "0 0 auto" }}>
          <HeaderUser />
        </span>
      </Flex>
    );
  }

  /* ยังไม่ได้เลือกกลุ่มงาน — แสดงรายชื่อกลุ่มงาน */
  if (!group) {
    return (
      <Flex align="center" gap={10} wrap={false} style={bar}>
        <Brand />
        {divider}
        <MenuStrip>
        <Link to="/modules" style={chip(pathname === "/modules")}>
          <AppstoreOutlined style={{ marginRight: 6 }} />
          ALL
        </Link>
        {groups.map((g) => (
          g.allowed ? (
            <Link key={g.key} to={`/modules/${g.key}`} style={chip(false)}>
              {g.title}
            </Link>
          ) : (
            <span key={g.key} style={deniedChip} title="ไม่มีสิทธิ์เข้าใช้งาน">
              <LockOutlined style={{ marginRight: 6 }} />
              {g.title}
            </span>
          )
        ))}
        </MenuStrip>
        <span style={{ flex: "0 0 auto" }}>
          <HeaderUser />
        </span>
      </Flex>
    );
  }

  /* เลือกกลุ่มงานแล้ว — แสดงเมนูย่อยของกลุ่มนั้น */
  return (
    <Flex align="center" gap={10} wrap={false} style={bar}>
      <Brand />
      {divider}
      <MenuStrip>
      <Link
        to="/modules"
        style={chip(false, { background: "#f5f7fa", borderColor: "#e2e8f0" })}
        title="กลับไปเลือกกลุ่มงาน"
      >
        <LeftOutlined style={{ marginRight: 6 }} />
        {group.code}
      </Link>

      <Link
        to={`/modules/${group.key}`}
        style={chip(pathname === `/modules/${group.key}`)}
      >
        {group.title}
      </Link>

      {group.items.map((item) => (
        item.allowed ? (
          <Link
            key={item.to}
            to={item.to}
            style={chip(pathname.startsWith(item.to))}
          >
            {item.title}
          </Link>
        ) : (
          <span key={item.to} style={deniedChip} title="ไม่มีสิทธิ์เข้าใช้งาน">
            <LockOutlined style={{ marginRight: 6 }} />
            {item.title}
          </span>
        )
      ))}

      </MenuStrip>
      <span style={{ flex: "0 0 auto" }}>
        <HeaderUser />
      </span>
    </Flex>
  );
}
